import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';

// Generic CRUD component used by all catalog pages
export default function CatalogoPage({ title, endpoint, fields, description }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      setItems(res.data.data);
    } catch (e) { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, [endpoint]);

  const openCreate = () => {
    const defaults = {};
    fields.forEach(f => { defaults[f.key] = f.default || ''; });
    setForm(defaults);
    setEditItem(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    const vals = {};
    fields.forEach(f => { vals[f.key] = item[f.key] ?? ''; });
    setForm(vals);
    setEditItem(item);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`${endpoint}/${editItem.id}`, form);
        toast.success('Actualizado correctamente');
      } else {
        await api.post(endpoint, form);
        toast.success('Creado correctamente');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Desactivar "${item.nombre}"?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success('Desactivado');
      fetchItems();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{title}</div>
          {description && <div className="page-subtitle">{description}</div>}
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14}/> Agregar</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div className="loading-screen"><div className="spinner"/></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  {fields.filter(f => !f.hidden).map(f => <th key={f.key}>{f.label}</th>)}
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={fields.length + 3} className="table-empty">Sin registros</td></tr>}
                {items.map(item => (
                  <tr key={item.id}>
                    <td style={{ color:'var(--text-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>{item.id}</td>
                    {fields.filter(f => !f.hidden).map(f => (
                      <td key={f.key}>
                        {f.type === 'color' ? (
                          <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ width:14, height:14, borderRadius:'50%', background:item[f.key] || '#ccc', border:'1px solid var(--border)' }}/>
                            <span style={{ fontFamily:'var(--font-mono)', fontSize:11 }}>{item[f.key]}</span>
                          </span>
                        ) : f.type === 'badge' ? (
                          <span className={`badge badge-${item[f.key]}`}>{item[f.key]}</span>
                        ) : (
                          <span style={{ fontSize:13 }}>{item[f.key] || <span className="text-muted">—</span>}</span>
                        )}
                      </td>
                    ))}
                    <td>
                      <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background:item.activo?'var(--success)':'var(--danger)' }}/>
                        <span style={{ fontSize:11, color:item.activo?'var(--success)':'var(--text-muted)' }}>{item.activo ? 'Activo' : 'Inactivo'}</span>
                      </span>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn-icon" onClick={() => openEdit(item)} title="Editar"><Pencil size={13}/></button>
                        <button className="btn-icon" onClick={() => handleDelete(item)} title="Desactivar" style={{ color:'var(--danger)', borderColor:'var(--danger-dim)' }}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">{editItem ? 'Editar' : 'Nuevo'} {title.replace(/s$/, '')}</span>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {fields.filter(f => !f.readonly).map(f => (
                  <div key={f.key} className="form-group">
                    <label className="form-label">{f.label}{f.required && ' *'}</label>
                    {f.type === 'textarea' ? (
                      <textarea className="form-control" value={form[f.key]||''} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))} required={f.required} rows={3} placeholder={f.placeholder}/>
                    ) : f.type === 'select' ? (
                      <select className="form-control" value={form[f.key]||''} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))} required={f.required}>
                        {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : f.type === 'color' ? (
                      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                        <input type="color" value={form[f.key]||'#cccccc'} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))} style={{ width:44, height:36, padding:2, background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'var(--radius)', cursor:'pointer' }}/>
                        <input className="form-control" value={form[f.key]||''} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))} placeholder="#f59e0b" style={{ flex:1 }}/>
                      </div>
                    ) : f.type === 'checkbox' ? (
                      <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm(p=>({...p,[f.key]:e.target.checked}))} style={{ width:16, height:16 }}/>
                        <span style={{ fontSize:13 }}>{f.checkLabel || 'Activo'}</span>
                      </label>
                    ) : (
                      <input className="form-control" type={f.type||'text'} value={form[f.key]||''} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))} required={f.required} placeholder={f.placeholder}/>
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
